import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import {
  perfilFieldId,
  REDES_SLOTS,
  REDES_VIDEO_SLOT,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import {
  SOCIAL_LABELS,
  SOCIAL_ORDER,
} from 'volto-ministerio-publico-federal/components/Icons/social';
import SocialUrlWidget, { type SocialPreview } from './SocialUrlWidget';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_redes_block',
    defaultMessage: 'Acompanhe nossas redes (capa)',
  },
  card: { id: 'mpf_redes_card', defaultMessage: 'Post {slot}' },
  cardVideo: {
    id: 'mpf_redes_card_video',
    defaultMessage: 'Post {slot} (vídeo)',
  },
  perfis: { id: 'mpf_redes_perfis', defaultMessage: 'Perfis oficiais' },
  url: { id: 'mpf_redes_url', defaultMessage: 'Endereço do post' },
  urlHelp: {
    id: 'mpf_redes_url_help',
    defaultMessage:
      'Cole o endereço do post. O ícone da rede é reconhecido na hora; use "Buscar dados" para tentar trazer título e capa.',
  },
  urlVideoHelp: {
    id: 'mpf_redes_url_video_help',
    defaultMessage:
      'Endereço do vídeo no YouTube. A capa é montada a partir do próprio vídeo, sem depender de busca.',
  },
  cardTitle: { id: 'mpf_redes_card_title', defaultMessage: 'Título' },
  cardTitleHelp: {
    id: 'mpf_redes_card_title_help',
    defaultMessage:
      'Aparece abaixo da imagem, em até duas linhas. Preenchido pela busca quando está vazio — o que você escrever nunca é sobrescrito.',
  },
  cardImage: { id: 'mpf_redes_card_image', defaultMessage: 'Capa' },
  cardImageHelp: {
    id: 'mpf_redes_card_image_help',
    defaultMessage:
      'Suba uma imagem, escolha uma do site ou cole um endereço. Use quando a capa trazida pela busca vier cortada ou ilegível.',
  },
  cardImageAlt: {
    id: 'mpf_redes_card_image_alt',
    defaultMessage: 'Texto alternativo da capa do Post {slot}',
  },
  cardImageAltHelp: {
    id: 'mpf_redes_card_image_alt_help',
    defaultMessage:
      'Obrigatório quando há capa. Descreva o que a imagem mostra, inclusive o texto escrito na arte. Lido por leitores de tela junto com o título do post.',
  },
  perfilUrl: { id: 'mpf_redes_perfil_url', defaultMessage: '{rede}' },
  perfilUrlHelp: {
    id: 'mpf_redes_perfil_url_help',
    defaultMessage: 'Vazio: o ícone não aparece no cabeçalho.',
  },
});

/** Ids dos campos de um card. Fonte única do nome dos campos. */
export const cardFieldIds = (slot: number) => ({
  url: `card${slot}Url`,
  title: `card${slot}Title`,
  image: `card${slot}Image`,
  imageAlt: `card${slot}ImageAlt`,
});

/** Todos os ids de campo de perfil, na ordem da fileira. */
export const PERFIL_FIELDS = SOCIAL_ORDER.map(perfilFieldId);

/**
 * Id do fieldset de cada slot.
 *
 * O `InlineForm` do Volto acessa `fieldsets.find(o => o.id === 'default').id`
 * sem guarda — um schema sem fieldset `default` derruba a sidebar inteira.
 * Como o cabeçalho da faixa é fixo no componente, não sobra campo avulso para
 * ocupar o `default`: o Post 1 mora nele, e os demais viram `card2`…`card5`.
 * Exportado porque o `Edit.tsx` usa o mesmo id para rolar a sidebar até o card
 * que o editor clicou.
 */
export const fieldsetIdForSlot = (slot: number): string =>
  slot === 1 ? 'default' : `card${slot}`;

export const PERFIS_FIELDSET_ID = 'perfis';

/**
 * Aplica o resultado da busca aos campos do card.
 *
 * Só preenche o que está vazio — mesma política de `buildLinkFields` em
 * `PublicacoesEditais/adapter.ts`. Clicar "Buscar dados" duas vezes não apaga
 * um título reescrito à mão; para trocar pelo valor automático, o editor limpa
 * o campo primeiro.
 */
export const applyPreviewToCard = (
  slot: number,
  data: Record<string, unknown>,
  preview: SocialPreview,
): Record<string, unknown> => {
  const ids = cardFieldIds(slot);
  const patch: Record<string, unknown> = {};
  if (preview.title && !data[ids.title]) patch[ids.title] = preview.title;
  if (preview.image && !data[ids.image]) patch[ids.image] = preview.image;
  return Object.keys(patch).length ? { ...data, ...patch } : data;
};

/**
 * Schema do bloco "Acompanhe nossas redes".
 *
 * Os cinco posts são slots FIXOS, em campos achatados (`card1Url`,
 * `card1Title`, …) e não numa lista: a grade 2+2+1 é estrutural, e o slot 5 *é*
 * o card grande de vídeo — não um card qualquer que por acaso caiu na quinta
 * posição. Não há caso de uso de reordenar, adicionar ou remover.
 *
 * A rede de cada card não é campo: sai do domínio da URL na hora de renderizar
 * (`resolveSocialNetwork`). Guardá-la abriria espaço para ficar dessincronizada
 * da URL, e o ganho seria nulo — são cinco regex por render.
 */
/**
 * Campos de ALT obrigatórios: o de cada post que tem capa gravada.
 *
 * Mesma regra do backend (`alt_imagem_obrigatorio.py`, que barra o save com
 * a lista do que falta) e das notícias (`helpers/Image/imageAltRequired`): o
 * ALT só é exigido quando a imagem existe. Vídeo do YouTube sem capa gravada
 * usa a miniatura automática e fica decorativo.
 */
export const requiredAltFields = (
  formData: Record<string, unknown> = {},
): string[] =>
  REDES_SLOTS.filter((slot) => !!formData[cardFieldIds(slot).image]).map(
    (slot) => cardFieldIds(slot).imageAlt,
  );

export const mpfAcompanheRedesSchema = ({
  intl,
  formData,
  onFetchPreview,
}: {
  intl: IntlShape;
  /** Dados do bloco: decidem quais ALTs são obrigatórios. */
  formData?: Record<string, unknown>;
  /** Recebe o resultado da busca de um card. Fornecido pelo `Data.tsx`. */
  onFetchPreview?: (slot: number, preview: SocialPreview) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    ...REDES_SLOTS.map((slot) => ({
      id: fieldsetIdForSlot(slot),
      title: intl.formatMessage(
        slot === REDES_VIDEO_SLOT ? messages.cardVideo : messages.card,
        { slot },
      ),
      fields: Object.values(cardFieldIds(slot)),
    })),
    {
      id: PERFIS_FIELDSET_ID,
      title: intl.formatMessage(messages.perfis),
      fields: PERFIL_FIELDS,
    },
  ],
  properties: {
    ...Object.fromEntries(
      REDES_SLOTS.flatMap((slot) => {
        const ids = cardFieldIds(slot);
        return [
          [
            ids.url,
            {
              title: intl.formatMessage(messages.url),
              description: intl.formatMessage(
                slot === REDES_VIDEO_SLOT
                  ? messages.urlVideoHelp
                  : messages.urlHelp,
              ),
              widget: SocialUrlWidget,
              // O `InlineForm` espalha o field spec inteiro como props do
              // widget, então o callback chega ao `SocialUrlWidget` por aqui —
              // mesmo mecanismo do `activeObject` em `PublicacoesEditais`.
              onFetch: onFetchPreview
                ? (preview: SocialPreview) => onFetchPreview(slot, preview)
                : undefined,
            },
          ],
          [
            ids.title,
            {
              title: intl.formatMessage(messages.cardTitle),
              description: intl.formatMessage(messages.cardTitleHelp),
              widget: 'charCount',
              recommendedLength: SEO_TITLE_LENGTH,
            },
          ],
          [
            ids.image,
            {
              title: intl.formatMessage(messages.cardImage),
              description: intl.formatMessage(messages.cardImageHelp),
              // `image` e não `object_browser`: este widget sobe arquivo,
              // navega no site E aceita endereço colado — e é um endereço
              // externo que a busca de metadados grava aqui.
              widget: 'image',
            },
          ],
          [
            ids.imageAlt,
            {
              // O número do post vai no título porque é ele que aparece no
              // aviso de erro do save ("Erro no campo … do bloco").
              title: intl.formatMessage(messages.cardImageAlt, { slot }),
              description: intl.formatMessage(messages.cardImageAltHelp),
              widget: 'textarea',
            },
          ],
        ];
      }),
    ),
    ...Object.fromEntries(
      SOCIAL_ORDER.map((network) => [
        perfilFieldId(network),
        {
          title: intl.formatMessage(messages.perfilUrl, {
            rede: SOCIAL_LABELS[network],
          }),
          description: intl.formatMessage(messages.perfilUrlHelp),
          widget: 'url',
        },
      ]),
    ),
  },
  required: requiredAltFields(formData),
});

export default mpfAcompanheRedesSchema;
